import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, X, Mail, Settings } from 'lucide-react';

interface ForwardingConfig {
  id: string;
  activity_type: string;
  forwarding_emails: string[];
  is_active: boolean;
}

const ACTIVITY_TYPES = [
  { value: 'contact', label: 'Contact Form', description: 'Emails from contact form submissions' },
  { value: 'quote', label: 'Quote Requests', description: 'Emails from quote form submissions' },
  { value: 'booking', label: 'Bookings', description: 'Emails from booking confirmations' },
  { value: 'general', label: 'General', description: 'All other email notifications' },
];

export default function EmailForwardingManager() {
  const [newEmails, setNewEmails] = useState<{ [key: string]: string }>({});
  const queryClient = useQueryClient();

  const { data: forwardingConfigs, isLoading } = useQuery({
    queryKey: ['email-forwarding-configs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_forwarding_configs')
        .select('*')
        .order('activity_type');
      
      if (error) throw error;
      return data as ForwardingConfig[];
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ForwardingConfig> }) => {
      const { error } = await supabase
        .from('email_forwarding_configs')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-forwarding-configs'] });
      toast.success('Forwarding configuration updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update configuration: ' + error.message);
    },
  });

  const addEmailToConfig = (configId: string, activityType: string) => {
    const email = newEmails[activityType]?.trim();
    if (!email) {
      toast.error('Please enter a valid email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email format');
      return;
    }

    const config = forwardingConfigs?.find(c => c.id === configId);
    if (!config) return;

    if (config.forwarding_emails.includes(email)) {
      toast.error('This email is already in the forwarding list');
      return;
    }

    const updatedEmails = [...config.forwarding_emails, email];
    updateConfigMutation.mutate({
      id: configId,
      updates: { forwarding_emails: updatedEmails }
    });

    setNewEmails(prev => ({ ...prev, [activityType]: '' }));
  };

  const removeEmailFromConfig = (configId: string, emailToRemove: string) => {
    const config = forwardingConfigs?.find(c => c.id === configId);
    if (!config) return;

    const updatedEmails = config.forwarding_emails.filter(email => email !== emailToRemove);
    updateConfigMutation.mutate({
      id: configId,
      updates: { forwarding_emails: updatedEmails }
    });
  };

  const toggleConfigStatus = (configId: string, isActive: boolean) => {
    updateConfigMutation.mutate({
      id: configId,
      updates: { is_active: isActive }
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Email Forwarding Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Email Forwarding Configuration
        </CardTitle>
        <CardDescription>
          Configure different forwarding email addresses for different types of activities
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {ACTIVITY_TYPES.map((activityType) => {
          const config = forwardingConfigs?.find(c => c.activity_type === activityType.value);
          if (!config) return null;

          return (
            <div key={activityType.value} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {activityType.label}
                  </h3>
                  <p className="text-sm text-muted-foreground">{activityType.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={config.is_active}
                    onCheckedChange={(checked) => toggleConfigStatus(config.id, checked)}
                  />
                  <Label className="text-sm">Active</Label>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Forwarding Email Addresses</Label>
                
                {/* Current emails */}
                <div className="flex flex-wrap gap-2">
                  {config.forwarding_emails.map((email, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {email}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => removeEmailFromConfig(config.id, email)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                  {config.forwarding_emails.length === 0 && (
                    <span className="text-sm text-muted-foreground">No forwarding emails configured</span>
                  )}
                </div>

                {/* Add new email */}
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={newEmails[activityType.value] || ''}
                    onChange={(e) => setNewEmails(prev => ({ 
                      ...prev, 
                      [activityType.value]: e.target.value 
                    }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addEmailToConfig(config.id, activityType.value);
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={() => addEmailToConfig(config.id, activityType.value)}
                    disabled={updateConfigMutation.isPending}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}